import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Cascader, CascaderProps } from "antd";

import { t } from "i18next";
import { Props } from "src/common/entity/props.entity";
import { MembershipCardEntity } from "src/module/membershipCard/entity/membership.card.entity";
import membershipCardService from "src/module/membershipCard/service/membership.card.service";
import styled from "@emotion/styled";

interface IProps extends Props {
	isMultiple?: boolean;
}

interface IOption {
	label: string | null;
	value: number | null;
	disabled?: boolean;
	children?: IOption[];
}

interface FlatItem {
	id: number;
	title: string;
	cardConfigTypeId: number;
	parentTitle: string | null;
	[key: string]: any;
}

const SubscriptionCascader: FC<IProps> = (props: IProps) => {
	const { SHOW_CHILD } = Cascader;
	const [responseData, setResponseData] = useState<FlatItem[]>([]);
	const [dataArr, setDataArr] = useState<IOption[]>([]);

	const multipleConfigs = useMemo(() => {
		if (props.isMultiple) {
			return {
				multiple: true,
				maxTagCount: "responsive",
				showCheckedStrategy: SHOW_CHILD,
			} as CascaderProps;
		}
	}, [SHOW_CHILD, props.isMultiple]);

	const getValue = useMemo(() => {
		if (!props.value) return void 0;
		const childIds = props.value?.split(",").map((item: string) => Number(item)) || [];
		return convertToParentChildPairs(childIds, responseData);
	}, [props.value, responseData]);

	const arrayToTree = (items: FlatItem[]): IOption[] => {
		const treeMap = new Map<string | null, IOption>();
		const result: IOption[] = [];

		items.forEach((item) => {
			const parentKey = `${item.parentTitle}_${item.cardConfigTypeId}`;
			if (!treeMap.has(parentKey)) {
				treeMap.set(parentKey, {
					label: item.parentTitle,
					value: item.cardConfigTypeId,
					children: [],
				});
				result.push(treeMap.get(parentKey)!);
			}
			treeMap.get(parentKey)!.children?.push({
				label: item.title,
				value: item.id,
			});
		});
		return result;
	};

	function convertToParentChildPairs(childIds: number[], dataSource: FlatItem[]): [number, number][] {
		return childIds
			.map((childId) => {
				const item = dataSource.find((item) => item.id === childId);
				if (!item) return void 0;
				const parentId = item.cardConfigTypeId;
				return [parentId, childId];
			})
			.filter(Boolean) as [number, number][];
	}

	const init = useCallback(async () => {
		const res = await membershipCardService.cascaderList();
		const resData = res.data.data as FlatItem[];
		setResponseData(resData);
		const treeData = arrayToTree(resData);
		setDataArr(treeData);
	}, []);

	useEffect(() => {
		init();
	}, [init]);

	const changeData = (e: any) => {
		const finalData = props.isMultiple ? e.map((item: number[]) => item[1]).join(",") : e?.length ? e[1].toString() : "";
		props.onChange?.(finalData);
	};

	const displayRender = (labels: string[], selectedOptions: any) =>
		labels.map((label, i) => {
			const option = selectedOptions[i];
			if (i === labels.length - 1) {
				return <span key={option.value}>{label}</span>;
			}
			return (
				<span key={option.value}>
					{`id:${option.value} - `}
					{label} {"> "}
				</span>
			);
		});

	return (
		<Container>
			{dataArr.length > 0 && (
				<Cascader
					value={getValue}
					onChange={changeData}
					allowClear
					placeholder={t("pleaseSelectMembershipCard")}
					{...multipleConfigs}
					options={dataArr}
					displayRender={displayRender}
				></Cascader>
			)}
		</Container>
	);
};

const Container = styled.div`
	.ant-select {
		width: 100%;
	}
`;

export default SubscriptionCascader;
